import EmptyStateNotice from '@/components/EmptyStateNotice.jsx';
import InputSelect from '@/components/InputSelect.jsx';
import { MdOutlineDashboardCustomize } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2.jsx';
import { DeleteIcon } from '@/lib/constants/icons.js';
import getLookupMethod from '@/lib/getLookupMethod.js';
import { postAiAction } from '@/redux/actions/gravityActions.js';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import { modal } from '@/redux/selectors/uiSelectors';
import argumentDataSources from '@/schema/argumentDataSources.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const CustomArgumentsModalWrapper = () => {
  const [data, setData] = useState({});
  const [promptRunning, setPromptRunning] = useState(false);
  const modalState = useSelector(memoizeSelector('modal:customArguments', (state) => modal(state, 'customArguments')));
  const { isLookup, lookupId, dataSavePath, parentKey, path, initialArguments, dataSource } = modalState || {};

  const activeStepAppActionCustomArgumentDefinition = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppAction(state)?.features?.customArguments);
  const activeAppActionCustomArgumentsDefinition = useSelector((state) => appScreenSelectors.activeAppAction(state)?.features?.customArguments);
  const lookupMethod = useMemo(() => getLookupMethod(dataSource, isLookup, 'list'), [dataSource, isLookup]);
  const { lookup, loading } = lookupMethod({ id: lookupId, lookupSettings: activeStepAppActionCustomArgumentDefinition || activeAppActionCustomArgumentsDefinition });

  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const placeholder = isLookup ? 'Select a field...' : 'Write a field key...';
  const argsArray = Object.entries(data);

  const handleSave = async (n, { useAi = false }) => {
    if (isLookup && !n) {
      return;
    }

    const addSingleArg = (arg) => {
      const key = arg?.value || arg;
      const value = arg?.label || arg;
      setData((prev) => ({ ...prev, [key]: value }));
      const payload = { type: 'lexical', value: '', custom: true, required: value?.required };
      const argKey = parentKey ? `${parentKey}.${key}` : key;
      actions.set(dataSavePath ? `${dataSavePath}.${path}.["${argKey}"]` : `${path}.["${argKey}"]`, payload);

      setTimeout(() => {
        document.querySelector('.active-argument:last-of-type')?.scrollIntoView({ behavior: 'smooth' });
      }, 10);
    };

    if (useAi) {
      setPromptRunning(true);
      const response = await postAiAction({
        action: 'getCustomArgumentsFromText',
        context: { query: n }
      });
      setPromptRunning(false);
      const args = response?.customArguments;
      args.forEach(addSingleArg);
      return;
    }

    addSingleArg(n);
  };

  const handleDelete = (key) => {
    setData((prev) => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });

    const argKey = parentKey ? `${parentKey}.${key}` : key;
    actions.set(dataSavePath ? `${dataSavePath}.${path}` : path, (prev) => {
      const { [argKey]: deleted, ...rest } = prev;
      return rest;
    });
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 to account for potential rounding errors
    e.target.setAttribute('data-scrolled', isScrolledToBottom.toString());
  };

  useEffect(() => {
    setData(
      initialArguments?.reduce((acc, arg) => {
        if (arg?.key && arg?.label) {
          acc[arg.key] = arg.label;
        }
        return acc;
      }, {})
    );
  }, [initialArguments]);

  return (
    <div className={'wrapper'}>
      <InputSelect
        activeOptions={argsArray}
        options={lookup} //
        placeholder={placeholder}
        aiPlaceholder={promptRunning ? 'Thinking...' : 'Use AI to add multiple field keys...'}
        onSave={handleSave}
        loading={loading}
      />
      {argsArray?.length > 0 && (
        <div className={'active-arguments'} onScroll={handleScroll}>
          {argsArray.map(([key, value]) => (
            <div key={key} className={'active-argument'}>
              <span>{value}</span>
              <IconV2 icon={DeleteIcon} className={'light-medium'} onClick={() => handleDelete(key)} title={'Delete Field'} />
            </div>
          ))}
        </div>
      )}
      {argsArray?.length === 0 && <EmptyStateNotice size="sm" icon={MdOutlineDashboardCustomize} loading={promptRunning} />}
    </div>
  );
};

export default CustomArgumentsModalWrapper;
